import { Box, Button, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Confirm } from 'src/components/common/Confirm';
import Loader from 'src/components/common/Loader';
import { PopupComponent } from 'src/components/common/PopupComponent';
import { useUser } from 'src/components/hooks/useUser';
import { useGetServerDateTimeMutation } from 'src/services/Utils';
import { hideLoader, showLoader } from 'src/store/Loader';
import { setMessage } from 'src/store/Message';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import {
  CommonVisitLogValidation,
  MonitoringVisitLogType,
  getLogValidationReset
} from 'src/types/MonitoringVisitLogType';
import { useSelectedAppInfo } from 'src/util/SelectedAppInfo';
import LogEditForm from './LogEditForm';
import './MonitoringLogs.scss';
import { useMonitoringVisitLog } from './useMonitoringVisitLog';

interface VisitLogEditDialogProps {
  isDialogOpen: boolean;
  editableLog: MonitoringVisitLogType;
  allVisitLogs: MonitoringVisitLogType[];
  visitTypesList: any;
  subVisitTypesList: any;
  onSuccess(): void;
  onClose(): void;
}

export default function VisitLogEditDialog(props: VisitLogEditDialogProps) {
  const { t } = useTranslation();
  const { user: currentUserName } = useUser();
  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const dispatch = useAppDispatch();
  const [visitLog, setVisitLog] = useState<MonitoringVisitLogType>(props.editableLog);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [validationObj, setValidationObj] = useState<CommonVisitLogValidation>(getLogValidationReset());
  const { updateMonitoringVisitActivity } = useMonitoringVisitLog();
  const { selectedAppSection } = useSelectedAppInfo();
  const [isEventEdited, setIsEventEdited] = useState(false);
  const [isActualDatesEdited, setIsActualDatesEdited] = useState(false);
  useEffect(() => {
    setVisitLog(props.editableLog);
  }, [props.editableLog]);
  const [getServerDateTime] = useGetServerDateTimeMutation();
  const isMandatoryFieldsSupplied = (eventToSave: MonitoringVisitLogType): boolean => {
    const tempArr = [];
    let tempValidArray = validationObj;
    if (isEventEdited) {
      if (eventToSave.start_date === '') {
        tempValidArray = { ...tempValidArray, start_date: true };
        tempArr.push(true);
      }
      if (eventToSave.end_date === '') {
        tempValidArray = { ...tempValidArray, end_date: true };
        tempArr.push(true);
      }
      if (eventToSave.sub_type === '') {
        tempValidArray = { ...tempValidArray, sub_type: true };
        tempArr.push(true);
      }
    } else if (isActualDatesEdited) {
      if (props.editableLog.actual_start_date && eventToSave.actual_start_date === '') {
        tempValidArray = { ...tempValidArray, actual_start_date: true };
        tempArr.push(true);
      }
      if (props.editableLog.actual_end_date && eventToSave.actual_end_date === '') {
        tempValidArray = { ...tempValidArray, actual_end_date: true };
        tempArr.push(true);
      }
    }

    setValidationObj(tempValidArray);
    if (tempArr.length > 0) {
      return !tempArr.some(e => e);
    } else {
      return true;
    }
  };

  const checkForSameEditEvent = (eventToSave: MonitoringVisitLogType): any => {
    const isSameEvent = props.allVisitLogs.some(
      (singleEvent: any) =>
        eventToSave.visit_type === singleEvent.visit_type &&
        eventToSave.sub_type === singleEvent.sub_type &&
        eventToSave.start_date === singleEvent.start_date &&
        eventToSave.end_date === singleEvent.end_date &&
        eventToSave.id === singleEvent.id //with matching id
    );
    const isSameEventWithMatchingSubType = props.allVisitLogs.some(
      (singleEvent: any) =>
        eventToSave.visit_type === singleEvent.visit_type &&
        eventToSave.sub_type === singleEvent.sub_type && //with matching subtype
        eventToSave.start_date === singleEvent.start_date &&
        eventToSave.end_date === singleEvent.end_date &&
        eventToSave.id !== singleEvent.id
    );
    const isSameEventWithoutMatchingSubType = props.allVisitLogs.some(
      (singleEvent: any) =>
        eventToSave.visit_type === singleEvent.visit_type &&
        eventToSave.start_date === singleEvent.start_date && //without matching subtype
        eventToSave.end_date === singleEvent.end_date &&
        eventToSave.id !== singleEvent.id
    );
    return {
      isSameEvent,
      matchingSubType: isSameEventWithMatchingSubType,
      withoutMatchingSubType: isSameEventWithoutMatchingSubType
    };
  };

  const checkForOverlapping = (eventToSave: MonitoringVisitLogType): boolean => {
    const isOverlapped = props.allVisitLogs.map((singleEvent: any) => {
      if (
        eventToSave.visit_type === singleEvent.visit_type &&
        // eventToSave.sub_type === singleEvent.sub_type &&
        eventToSave.id !== singleEvent.id
      ) {
        const currentEventStartDate = moment(eventToSave.start_date);
        const currentEventEndDate = moment(eventToSave.end_date);
        const existingEventStartDate = moment(singleEvent.start_date);
        const existingEventEndDate = moment(singleEvent.end_date);
        if (
          (currentEventStartDate >= existingEventStartDate && currentEventStartDate <= existingEventEndDate) ||
          (currentEventEndDate >= existingEventStartDate && currentEventEndDate <= existingEventEndDate) ||
          (existingEventStartDate >= currentEventStartDate && existingEventStartDate <= currentEventEndDate) ||
          (existingEventEndDate >= currentEventStartDate && existingEventEndDate <= currentEventEndDate)
        ) {
          return true;
        } else {
          return false;
        }
      }
    });

    if (!isOverlapped.includes(true)) {
      return true;
    } else {
      return false;
    }
  };

  const updateMonitoringLog = async (eventToSave: MonitoringVisitLogType) => {
    const serverDateTime: any = await getServerDateTime();
    if (
      !isEventEdited &&
      ((eventToSave.actual_start_date && moment(eventToSave.actual_start_date) > moment(`${serverDateTime.data}Z`)) ||
        (eventToSave.actual_end_date && moment(eventToSave.actual_end_date) > moment(`${serverDateTime.data}Z`)))
    ) {
      dispatch(
        setMessage({
          msgDetails: `Actual Start and End date & time is not in sync with Server date & time.`,
          severity: 'error'
        })
      );
      return;
    } else {
      dispatch(showLoader());
      const updateResponse: any = await updateMonitoringVisitActivity({
        id: eventToSave.id,
        site_id: selectedSite,
        study_id: studyId,
        country: selectedCountry,
        application_section: selectedAppSection,
        data: {
          id: eventToSave.id,
          visit_type: eventToSave.visit_type,
          sub_type: eventToSave.sub_type,
          start_date: eventToSave.start_date,
          end_date: eventToSave.end_date,
          actual_start_date: isEventEdited ? null : eventToSave.actual_start_date,
          actual_end_date: isEventEdited ? null : eventToSave.actual_end_date,
          updated_by: currentUserName
        }
      });
      if (updateResponse?.data) {
        dispatch(
          setMessage({
            msgDetails: 'Visit updated sucessfully',
            severity: 'success'
          })
        );
        props.onSuccess();
        props.onClose();
      } else if (updateResponse?.error) {
        if (updateResponse?.error.status == 500) {
          dispatch(
            setMessage({
              msgDetails: `${updateResponse?.error.status} : Internal Server Error`,
              severity: 'error'
            })
          );
        } else if (updateResponse?.error.status == 403) {
          dispatch(
            setMessage({
              msgDetails: 'Error 403 Restricted User permission',
              severity: 'error'
            })
          );
        }
      }
      setIsEventEdited(false);
      setIsActualDatesEdited(false);
      dispatch(hideLoader());
    }
  };
  const saveEvent = async (eventToSave: MonitoringVisitLogType) => {
    if (isEventEdited || isActualDatesEdited) {
      if (isEventEdited && isMandatoryFieldsSupplied(eventToSave)) {
        if (eventToSave.start_date === 'Invalid date' || eventToSave.end_date === 'Invalid date') {
          setValidationObj({
            ...validationObj,
            end_date: eventToSave.end_date === 'Invalid date',
            start_date: eventToSave.start_date === 'Invalid date'
          });
          dispatch(
            setMessage({
              msgDetails: 'Invalid Date(s)',
              severity: 'error'
            })
          );
        } else if (moment(eventToSave.start_date).isSame(moment(eventToSave.end_date))) {
          dispatch(
            setMessage({
              msgDetails: 'Scheduled start date and time should not be equal to scheduled end date and time',
              severity: 'error'
            })
          );
        } else if (moment(eventToSave.start_date) > moment(eventToSave.end_date)) {
          dispatch(
            setMessage({
              msgDetails: 'Scheduled start date and time must be less than scheduled end date and time',
              severity: 'error'
            })
          );
        } else {
          let sameEventValidation: any = {};
          sameEventValidation = checkForSameEditEvent(eventToSave);
          if (
            !sameEventValidation.isSameEvent &&
            !sameEventValidation.matchingSubType &&
            !sameEventValidation.withoutMatchingSubType
          ) {
            if (checkForOverlapping(eventToSave)) {
              setIsConfirmOpen(true);
            } else {
              dispatch(
                setMessage({
                  msgDetails: 'Visits overlapped',
                  severity: 'error'
                })
              );
            }
          } else {
            displayVisitExistsMessageForUpdate(sameEventValidation);
          }
        }
      } else if (isActualDatesEdited && isMandatoryFieldsSupplied(eventToSave)) {
        if (eventToSave.actual_start_date === 'Invalid date' || eventToSave.actual_end_date === 'Invalid date') {
          setValidationObj({
            ...validationObj,
            actual_end_date: eventToSave.actual_end_date === 'Invalid date',
            actual_start_date: eventToSave.actual_start_date === 'Invalid date'
          });
          dispatch(
            setMessage({
              msgDetails: 'Invalid Date(s)',
              severity: 'error'
            })
          );
        } else if (
          props.editableLog.actual_start_date &&
          props.editableLog.actual_end_date &&
          moment(eventToSave.actual_start_date).isSame(moment(eventToSave.actual_end_date))
        ) {
          dispatch(
            setMessage({
              msgDetails: 'Actual start date and time should not be equal to actual end date and time',
              severity: 'error'
            })
          );
        } else if (
          props.editableLog.actual_start_date &&
          props.editableLog.actual_end_date &&
          moment(eventToSave.actual_start_date) > moment(eventToSave.actual_end_date)
        ) {
          dispatch(
            setMessage({
              msgDetails: 'Actual start date and time must be less than actual end date and time',
              severity: 'error'
            })
          );
        } else if (
          props.editableLog.actual_start_date &&
          moment(eventToSave.actual_start_date) < moment(eventToSave.start_date)
        ) {
          dispatch(
            setMessage({
              msgDetails: 'Actual start date and time must be greater than scheduled start date and time',
              severity: 'error'
            })
          );
        } else {
          setIsConfirmOpen(true);
        }
      }
    } else {
      dispatch(setMessage({ msgDetails: 'No data modified', severity: 'success' }));
    }
    function displayVisitExistsMessageForUpdate(eventMatchingObj: any) {
      dispatch(
        setMessage({
          msgDetails: eventMatchingObj.isSameEvent
            ? 'No data modified'
            : eventMatchingObj.matchingSubType
            ? 'Record already exists'
            : eventMatchingObj.withoutMatchingSubType
            ? 'Visit already created with the same start date and time, End date and time'
            : 'Checking',
          severity: eventMatchingObj.isSameEvent ? 'success' : 'error'
        })
      );
    }
  };

  return (
    <PopupComponent className="popup-component" open={props.isDialogOpen}>
      <Loader />
      <DialogTitle>
        Schedule:{' '}
        {visitLog.start_date !== '' && visitLog.end_date !== ''
          ? `${moment(visitLog.start_date).format('LL')} to ${moment(visitLog.end_date).format('LL')}`
          : visitLog.start_date !== ''
          ? `${moment(visitLog.start_date).format('LL')}`
          : ``}
      </DialogTitle>
      <DialogContent>
        <LogEditForm
          visitLog={visitLog}
          setVisitLog={setVisitLog}
          visitTypesList={props.visitTypesList}
          subVisitTypesList={props.subVisitTypesList}
          validationObj={validationObj}
          setValidationObj={setValidationObj}
          isEventEdited={isEventEdited}
          setIsEventEdited={setIsEventEdited}
          isActualDatesEdited={isActualDatesEdited}
          setIsActualDatesEdited={setIsActualDatesEdited}
        />
      </DialogContent>
      <DialogActions>
        <Box flexGrow={1} />
        <br />
        <Button onClick={props.onClose} className="grey-btn">
          {t('Close')}
        </Button>
        <Button onClick={() => saveEvent(visitLog)}>{t('Save')}</Button>
      </DialogActions>
      <Confirm
        isOpen={isConfirmOpen}
        message={
          isActualDatesEdited
            ? `Are you sure do you want to update the check-in and check-out date?`
            : isEventEdited
            ? 'Are you sure do you want to update the visit?'
            : ''
        }
        confirmButtonText="yes"
        cancelButtonText="no"
        onCancel={() => setIsConfirmOpen(false)}
        onConfirm={() => {
          setIsConfirmOpen(false);
          updateMonitoringLog(visitLog);
        }}
      />
    </PopupComponent>
  );
}
